import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { shouldShowStudyMaterials } from "@lms/backend/lib/lessons/study-material-labels";
import { AiTutorPanel } from "@/components/ai-tutor-panel";
import { CohortLivePanel } from "@/components/cohort-live-panel";
import { CourseOutlineSidebar, type OutlineLessonState } from "@/components/course-outline-sidebar";
import { CourseResourcesPanel } from "@/components/course-resources-panel";
import { StudentMetricsPanel } from "@/components/student-metrics-panel";
import { ForumPanel } from "@/components/forum-panel";
import { HomeworkPlayer } from "@/components/homework-player";
import { LiveSessionPanel } from "@/components/live-session-panel";
import { BunnyLessonPlayer } from "@/components/bunny-lesson-player";
import { LessonProgressTracker } from "@/components/lesson-progress-tracker";
import { ProtectedPlayerFrame } from "@/components/video/protected-player-frame";
import { PlayingElsewhereOverlay } from "@/components/video/playing-elsewhere-overlay";
import { usePlaybackLease } from "@/hooks/use-playback-lease";
import { htmlVideoClock, type PlaybackClock } from "@/lib/playback-clock";
import { QuizPlayer } from "@/components/quiz-player";
import { StudyNotesPanels } from "@/components/study-notes-panels";
import { useAuthStore } from "@/stores/authStore";
import {
  type LessonContent,
  type LessonProgressResult,
  type PlayerModule,
  type PlayerOutline,
} from "@/services/api/player";
import { apiErrorText } from "@/lib/api-error";
import { ApiDownloadButton } from "@/components/api-download-button";
import { useLocalPlaybackUrl } from "@/hooks/use-local-playback-url";

type CoursePlayerProps = {
  tenantSlug: string;
  courseSlug: string;
  courseId: string;
  courseTitle: string;
  activeLessonId: string;
  modules: PlayerModule[];
  lessonContent: LessonContent | null;
  cohortName?: string | null;
};

function formatUnlockDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** LRN-10: "12:30" / "1:02:03" for the resume notice. */
export function formatPlaybackTime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = String(s % 60).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`
    : `${minutes}:${seconds}`;
}

/** The player's clock, tagged with the lesson it plays (a lesson change swaps the player). */
type LessonClock = { lessonId: string; clock: PlaybackClock };

/** A media-element failure has no API error; it gets the generic "couldn't load" copy. */
const MEDIA_ERROR = Symbol("media-error");

export function CoursePlayer({
  tenantSlug,
  courseSlug,
  courseId,
  courseTitle,
  activeLessonId,
  modules,
  lessonContent,
  cohortName,
}: CoursePlayerProps) {
  const t = useTranslations("player");
  const tAnnouncements = useTranslations("announcements");
  const tRoot = useTranslations();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const [lessonClock, setLessonClock] = useState<LessonClock | null>(null);
  // Only the active lesson's player counts — never the one being torn down.
  const clock = lessonClock?.lessonId === activeLessonId ? lessonClock.clock : null;
  // LRN-16: the error belongs to one lesson and is dropped when the student moves on.
  const [videoError, setVideoError] = useState<{ lessonId: string; error: unknown } | null>(null);
  // A callback ref per lesson (the <video> is keyed by lesson), so the tracker re-subscribes and
  // the resume seek targets the new element.
  const videoRef = useCallback(
    (video: HTMLVideoElement | null) =>
      setLessonClock(video ? { lessonId: activeLessonId, clock: htmlVideoClock(video) } : null),
    [activeLessonId],
  );
  const setBunnyClock = useCallback(
    (next: PlaybackClock | null) =>
      setLessonClock(next ? { lessonId: activeLessonId, clock: next } : null),
    [activeLessonId],
  );
  const showVideoError = useCallback(
    (error?: unknown) => setVideoError({ lessonId: activeLessonId, error: error ?? MEDIA_ERROR }),
    [activeLessonId],
  );
  const role = useAuthStore((state) => state.user?.role);
  // Plan 6 S10: the tutor shows only where the school has it on (a missing flag = hidden).
  const { tenant: schoolTenant } = useSchoolBootstrap();
  const showAiTutor = role === "student" && schoolTenant?.aiTutorEnabled === true;
  // H3: one video at a time per student account (staff previews are not limited).
  const { blocked: playingElsewhere, playHere } = usePlaybackLease(
    role === "student" ? clock : null,
  );
  const leaseOverlay = playingElsewhere ? <PlayingElsewhereOverlay onPlayHere={playHere} /> : null;

  // LRN-21 / LRN-10: per-lesson completed / in-progress marks come with the outline.
  const lessonStates = useMemo(() => {
    const map: Record<string, OutlineLessonState> = {};
    for (const mod of modules) {
      for (const lesson of mod.lessons) {
        if (lesson.progress)
          map[lesson.id] = lesson.progress.completed ? "completed" : "in_progress";
      }
    }
    return map;
  }, [modules]);
  // Each saved post updates the cached outline, so the marks (and the resume position when the
  // student comes back to this lesson) stay current without refetching.
  const applySavedProgress = useCallback(
    (result: LessonProgressResult) => {
      queryClient.setQueryData<PlayerOutline>(["player-outline", courseSlug], (outline) => {
        if (!outline) return outline;
        return {
          ...outline,
          modules: outline.modules.map((mod) => ({
            ...mod,
            lessons: mod.lessons.map((lesson) =>
              lesson.id === result.lessonId
                ? {
                    ...lesson,
                    progress: {
                      completed: result.completed,
                      watchedSeconds: result.watchedSeconds,
                      positionSeconds:
                        result.positionSeconds ?? lesson.progress?.positionSeconds ?? 0,
                      lastViewedAt: new Date().toISOString(),
                    },
                  }
                : lesson,
            ),
          })),
        };
      });
    },
    [courseSlug, queryClient],
  );

  const allLessons = useMemo(() => modules.flatMap((mod) => mod.lessons), [modules]);
  const activeIndex = allLessons.findIndex((lesson) => lesson.id === activeLessonId);
  const activeLesson = activeIndex >= 0 ? allLessons[activeIndex] : undefined;
  const previousLesson = activeIndex > 0 ? allLessons[activeIndex - 1] : undefined;
  const nextLesson =
    activeIndex >= 0 && activeIndex < allLessons.length - 1
      ? allLessons[activeIndex + 1]
      : undefined;
  const isLocked = Boolean(activeLesson?.isLocked);
  const savedPosition = activeLesson?.progress?.positionSeconds ?? 0;

  // LRN-10: once per lesson visit, the player jumps to where the student stopped. Later saves
  // (which update the cached position) don't move it again; a Bunny iframe refresh restores its
  // own position (BunnyLessonPlayer).
  const seekedLessonRef = useRef<string | null>(null);
  const startPositionRef = useRef(savedPosition);
  if (seekedLessonRef.current !== activeLessonId) startPositionRef.current = savedPosition;
  const [resumedAt, setResumedAt] = useState<{ lessonId: string; seconds: number } | null>(null);
  useEffect(() => {
    if (!clock || seekedLessonRef.current === activeLessonId) return;
    seekedLessonRef.current = activeLessonId;
    const start = startPositionRef.current;
    if (start > 0) {
      clock.seek(start);
      setResumedAt({ lessonId: activeLessonId, seconds: start });
    }
  }, [activeLessonId, clock]);
  useEffect(() => {
    // Leaving a lesson forgets the seek, so coming back resumes again.
    return () => {
      seekedLessonRef.current = null;
    };
  }, [activeLessonId]);
  const resumeNotice = resumedAt?.lessonId === activeLessonId ? resumedAt.seconds : null;
  const startOver = () => {
    clock?.seek(0);
    setResumedAt(null);
  };
  const playsLocalVideo = Boolean(
    !isLocked && activeLesson?.hasVideo && activeLesson.videoProvider !== "bunny",
  );
  // C1: a tokenised, API-base URL — `<video>` can't send the Bearer token itself.
  const localVideoUrl = useLocalPlaybackUrl(activeLessonId, playsLocalVideo, showVideoError);
  const hasStudyNotes = Boolean(
    lessonContent?.bodyHtml?.trim() || lessonContent?.bodyHtmlHi?.trim(),
  );
  const videoErrorText =
    videoError && videoError.lessonId === activeLessonId
      ? videoError.error === MEDIA_ERROR
        ? t("videoLoadError")
        : apiErrorText(videoError.error, tRoot, "player.videoLoadError", {
            namespace: "player.errors",
          })
      : null;
  const lessonHref = (lessonId: string) =>
    `/t/${tenantSlug}/learn/${courseSlug}/lessons/${lessonId}`;

  return (
    <div
      className="grid gap-6 lg:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] lg:items-start"
      data-testid="course-player"
    >
      {/* LRN-18 / R3-L4: the lesson comes first in the DOM (reading and tab order, WCAG 1.3.2 /
          2.4.3) and on phones; only the desktop grid moves the outline to the left column. */}
      <div className="min-w-0 space-y-8 lg:order-2" data-testid="course-player-content">
        {!isLocked ? (
          <LessonProgressTracker
            lessonId={activeLessonId}
            hasVideo={Boolean(activeLesson?.hasVideo)}
            isTextLesson={activeLesson?.contentType === "text"}
            clock={clock}
            initialPositionSeconds={startPositionRef.current}
            onSaved={applySavedProgress}
          />
        ) : null}

        <header>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-[var(--text-muted)]">{courseTitle}</p>
            <div className="flex gap-4">
              <Link
                to={`/t/${tenantSlug}/learn/${courseSlug}/community?lessonId=all`}
                className="text-sm font-medium text-[var(--text-heading)] hover:underline"
                data-testid="player-community-link"
              >
                {t("community")}
              </Link>
              {/* COMM-3 (W3): the course's announcements page. */}
              <Link
                to={`/t/${tenantSlug}/learn/${courseSlug}/announcements`}
                className="text-sm font-medium text-[var(--text-heading)] hover:underline"
                data-testid="player-announcements-link"
              >
                {tAnnouncements("navAnnouncements")}
              </Link>
            </div>
          </div>
          {cohortName ? (
            <p
              className="mt-1 text-sm text-[var(--text-secondary)]"
              data-testid="player-cohort-label"
            >
              {t("cohortLabel", { name: cohortName })}
            </p>
          ) : null}
          <h1 className="font-serif text-2xl font-semibold text-[var(--brand-heading)]">
            {activeLesson?.displayTitle ?? activeLesson?.title ?? t("lessonFallback")}
          </h1>
          {activeLesson?.subtitle ? (
            <p
              className="mt-1 text-sm text-[var(--brand-muted)]"
              data-testid="player-session-topic"
            >
              {activeLesson.subtitle}
            </p>
          ) : null}
        </header>

        {isLocked ? (
          <div
            role="status"
            className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-soft)] p-4 text-sm text-[var(--text-heading)]"
            data-testid="course-player-locked"
          >
            <p className="font-medium">{t("lockedTitle")}</p>
            {activeLesson?.unlockAt ? (
              <p className="mt-1" data-testid="course-player-unlock-at">
                {t("availableFrom", { date: formatUnlockDate(activeLesson.unlockAt, locale) })}
              </p>
            ) : null}
          </div>
        ) : null}

        {videoErrorText ? (
          <p
            role="alert"
            className="text-sm text-[var(--danger)]"
            data-testid="course-player-video-error"
          >
            {videoErrorText}
          </p>
        ) : null}

        {/* H3: both players sit in the watermarked frame, which also owns full screen. */}
        {!isLocked && activeLesson?.hasVideo && activeLesson.videoProvider === "bunny" ? (
          <ProtectedPlayerFrame overlay={leaseOverlay}>
            <BunnyLessonPlayer
              key={activeLessonId}
              lessonId={activeLessonId}
              title={activeLesson.displayTitle ?? activeLesson.title}
              onClock={setBunnyClock}
              onError={showVideoError}
            />
          </ProtectedPlayerFrame>
        ) : null}

        {playsLocalVideo ? (
          <ProtectedPlayerFrame overlay={leaseOverlay}>
            <video
              key={activeLessonId}
              ref={videoRef}
              data-testid="course-player-video"
              className="aspect-video w-full rounded-lg bg-black"
              controls
              playsInline
              // The browser's own full screen / picture-in-picture would leave the watermark
              // behind; the frame's button is used instead.
              controlsList="nofullscreen nodownload noremoteplayback"
              disablePictureInPicture
              // Owner decision (decisions §32): no casting — no Remote Playback (Chromecast and
              // the like) and no AirPlay; a TV would show the video without the watermark.
              disableRemotePlayback
              x-webkit-airplay="deny"
              src={localVideoUrl ?? undefined}
              onError={() => showVideoError()}
            />
          </ProtectedPlayerFrame>
        ) : null}

        {resumeNotice !== null ? (
          <p
            role="status"
            className="flex flex-wrap items-center gap-3 text-sm text-[var(--text-secondary)]"
            data-testid="player-resumed-at"
          >
            <span>{t("resumedAt", { time: formatPlaybackTime(resumeNotice) })}</span>
            <button
              type="button"
              onClick={startOver}
              className="font-medium text-[var(--text-heading)] underline"
              data-testid="player-start-over"
            >
              {t("startOver")}
            </button>
          </p>
        ) : null}

        {!isLocked && hasStudyNotes && lessonContent ? (
          <StudyNotesPanels
            bodyHtml={lessonContent.bodyHtml}
            bodyHtmlHi={lessonContent.bodyHtmlHi}
            lessonId={activeLessonId}
          />
        ) : null}

        {!isLocked &&
        lessonContent &&
        shouldShowStudyMaterials(lessonContent.attachments.length) ? (
          <section
            data-testid="course-player-study-materials"
            aria-labelledby="course-player-study-materials-heading"
          >
            <div data-testid="course-player-attachments">
              <h2
                id="course-player-study-materials-heading"
                className="text-lg font-medium"
                data-testid="course-player-study-materials-heading"
              >
                {t("studyMaterial")}
              </h2>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{t("studyMaterialHint")}</p>
              <ul className="mt-2 space-y-2">
                {lessonContent.attachments.map((file) => (
                  <li key={file.id}>
                    <ApiDownloadButton
                      path={`/v1/lessons/${activeLessonId}/attachments/${file.id}/download?as=link`}
                      fileName={file.fileName}
                      className="text-left text-[var(--text-heading)] underline"
                      data-testid={`player-attachment-${file.id}`}
                    >
                      {file.fileName}
                    </ApiDownloadButton>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        {!isLocked ? <CourseResourcesPanel courseId={courseId} /> : null}

        {!isLocked && activeLesson?.contentType === "quiz" ? (
          <QuizPlayer lessonId={activeLessonId} />
        ) : null}

        {!isLocked && activeLesson?.contentType === "assignment" ? (
          <HomeworkPlayer lessonId={activeLessonId} />
        ) : null}

        {!isLocked && activeLesson?.contentType === "live_session" ? (
          <LiveSessionPanel lessonId={activeLessonId} hasVideo={Boolean(activeLesson?.hasVideo)} />
        ) : null}

        {!isLocked &&
        (activeLesson?.contentType === "text" || activeLesson?.hasVideo) &&
        !hasStudyNotes ? (
          <p className="text-sm text-[var(--text-secondary)]" data-testid="course-player-no-text">
            {t("noNotes")}
          </p>
        ) : null}

        {!isLocked &&
        !activeLesson?.hasVideo &&
        activeLesson?.contentType !== "text" &&
        activeLesson?.contentType !== "quiz" &&
        activeLesson?.contentType !== "assignment" &&
        activeLesson?.contentType !== "live_session" ? (
          <p className="text-sm text-[var(--text-secondary)]" data-testid="course-player-no-video">
            {activeLesson?.completionState === "awaiting_video"
              ? t("videoAwaited")
              : activeLesson?.completionState === "not_counted"
                ? `${t("noVideo")} ${t("noVideoNotCounted")}`
                : t("noVideo")}
          </p>
        ) : null}

        {/* R-3: a live session whose recording is on its way holds the course too. */}
        {!isLocked &&
        !activeLesson?.hasVideo &&
        activeLesson?.contentType === "live_session" &&
        activeLesson.completionState === "awaiting_video" ? (
          <p
            className="text-sm text-[var(--text-secondary)]"
            data-testid="course-player-video-awaited"
          >
            {t("videoAwaited")}
          </p>
        ) : null}

        {!isLocked && showAiTutor ? (
          <AiTutorPanel
            courseId={courseId}
            tenantSlug={tenantSlug}
            lessonId={activeLessonId}
            lessonTitle={activeLesson?.displayTitle ?? activeLesson?.title ?? t("lessonFallback")}
          />
        ) : null}

        {!isLocked ? <ForumPanel courseId={courseId} lessonId={activeLessonId} /> : null}

        {!isLocked ? <CohortLivePanel courseId={courseId} /> : null}

        {!isLocked ? <StudentMetricsPanel courseId={courseId} /> : null}

        {previousLesson || nextLesson ? (
          <nav
            aria-label={t("lessonNavigation")}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4"
          >
            {previousLesson ? (
              <Link
                to={lessonHref(previousLesson.id)}
                className="text-sm font-medium text-[var(--text-heading)] hover:underline"
                data-testid="course-player-prev"
              >
                ← {t("previousLesson")}
                <span className="sr-only">
                  : {previousLesson.displayTitle ?? previousLesson.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {nextLesson ? (
              <Link
                to={lessonHref(nextLesson.id)}
                className="text-sm font-medium text-[var(--text-heading)] hover:underline"
                data-testid="course-player-next"
              >
                {t("nextLesson")} →
                <span className="sr-only">: {nextLesson.displayTitle ?? nextLesson.title}</span>
              </Link>
            ) : null}
          </nav>
        ) : null}
      </div>

      <div className="lg:order-1">
        <CourseOutlineSidebar
          tenantSlug={tenantSlug}
          courseSlug={courseSlug}
          courseTitle={courseTitle}
          activeLessonId={activeLessonId}
          modules={modules}
          lessonStates={lessonStates}
        />
      </div>
    </div>
  );
}
